// Copyright (C) 2024 Browser Video Implementation for Cloudflare Streaming

const React = require('react');
const PropTypes = require('prop-types');
const browserStreamingService = require('stremio/common/browser-streaming');
const EventEmitter = require('eventemitter3');

class BrowserVideo {
    constructor() {
        this.events = new EventEmitter();
        this.videoElement = null;
        this.containerElement = null;
        this.state = {
            paused: true,
            time: 0,
            duration: 0,
            buffering: false,
            buffered: [],
            volume: 1,
            muted: false,
            playbackSpeed: 1,
            audioTracks: [],
            selectedAudioTrackId: null,
            subtitlesTracks: [],
            selectedSubtitlesTrackId: null,
            manifest: null,
            stream: null
        };
        this.props = [
            'paused', 'time', 'duration', 'buffering', 'buffered', 
            'volume', 'muted', 'playbackSpeed', 'audioTracks', 
            'selectedAudioTrackId', 'subtitlesTracks', 'selectedSubtitlesTrackId'
        ];
    }

    on(event, handler) {
        this.events.on(event, handler);
    }

    off(event, handler) {
        this.events.off(event, handler);
    }

    dispatch(action, options = {}) {
        try {
            switch (action.type) {
                case 'command':
                    this.handleCommand(action, options);
                    break;
                case 'setProp':
                    this.setProp(action.propName, action.propValue);
                    break;
                case 'observeProp':
                    this.observeProp(action.propName);
                    break;
            }
        } catch (error) {
            console.error('BrowserVideo dispatch error:', error);
            this.events.emit('error', error);
        }
    }

    handleCommand(action, options) {
        switch (action.commandName) {
            case 'load':
                this.load(action.commandArgs, options);
                break;
            case 'unload':
                this.unload();
                break;
            case 'addExtraSubtitlesTracks':
                this.addExtraSubtitlesTracks(action.commandArgs.tracks);
                break;
            case 'addLocalSubtitles':
                this.addLocalSubtitles(action.commandArgs.filename, action.commandArgs.buffer);
                break;
        }
    }

    async load(args, options) {
        try {
            console.log('BrowserVideo loading stream:', args);
            
            if (!options.containerElement) {
                throw new Error('Container element is required');
            }

            this.containerElement = options.containerElement;
            
            // Create video element if it doesn't exist
            if (!this.videoElement) {
                this.videoElement = document.createElement('video');
                this.videoElement.style.width = '100%';
                this.videoElement.style.height = '100%';
                this.videoElement.style.objectFit = 'contain';
                this.videoElement.controls = false;
                this.videoElement.crossOrigin = 'anonymous';
                
                // Set up event listeners
                this.setupVideoEventListeners();
                
                this.containerElement.appendChild(this.videoElement);
            }

            // Check if we have a stream to play
            console.log('Stream object structure:', args.stream);
            console.log('Load arguments:', args);
            
            // Extract video URL from different possible stream formats
            let videoUrl = null;
            
            if (args.stream) {
                // Try different possible URL locations
                if (args.stream.behaviorHints && args.stream.behaviorHints.videoUrl) {
                    videoUrl = args.stream.behaviorHints.videoUrl;
                } else if (args.stream.url) {
                    videoUrl = args.stream.url;
                } else if (args.streamingServerURL) {
                    // Use the streaming server URL from Player component
                    videoUrl = args.streamingServerURL;
                    console.log('Using streaming server URL from Player:', videoUrl);
                } else if (args.stream.infoHash) {
                    // This is a torrent stream - we need to use the streaming server
                    console.log('Torrent stream detected, using streaming server wrapper');
                    throw new Error('Torrent streams require streaming server - using wrapper');
                } else {
                    console.log('No video URL found in stream, checking streaming server');
                    throw new Error('No video URL found in stream - using streaming server wrapper');
                }
            }
            
            if (videoUrl) {
                
                // Use browser streaming service
                if (browserStreamingService.enableBrowserStreaming) {
                    console.log('Using browser streaming service for:', videoUrl);
                    
                    const result = await browserStreamingService.streamVideo(videoUrl, this.videoElement);
                    
                    if (result.success) {
                        this.state.stream = args.stream;
                        this.state.manifest = {
                            name: 'Browser Video',
                            props: this.props
                        };
                        
                        this.events.emit('implementationChanged', this.state.manifest);
                        
                        // Start playback
                        if (args.autoplay) {
                            this.videoElement.play().catch(error => {
                                console.error('Autoplay failed:', error);
                            });
                        }
                        
                        console.log('Browser streaming started successfully');
                    } else {
                        throw new Error(result.error || 'Browser streaming failed');
                    }
                } else {
                    // Direct video source
                    this.videoElement.src = videoUrl;
                    this.videoElement.crossOrigin = 'anonymous';
                    
                    if (args.autoplay) {
                        this.videoElement.play().catch(error => {
                            console.error('Autoplay failed:', error);
                        });
                    }
                }
            } else {
                // Let the streaming server wrapper handle this case
                console.log('No video URL found, delegating to streaming server wrapper');
                throw new Error('No video URL found - delegating to streaming server wrapper');
            }
            
        } catch (error) {
            console.error('BrowserVideo load error:', error);
            // Don't emit error here, let the streaming server wrapper handle it
            throw error;
        }
    }

    setupVideoEventListeners() {
        if (!this.videoElement) return;

        this.videoElement.addEventListener('loadedmetadata', () => {
            this.state.duration = this.videoElement.duration;
            this.events.emit('propChanged', 'duration', this.state.duration);
        });

        this.videoElement.addEventListener('timeupdate', () => {
            this.state.time = this.videoElement.currentTime;
            this.events.emit('propChanged', 'time', this.state.time);
        });

        this.videoElement.addEventListener('play', () => {
            this.state.paused = false;
            this.events.emit('propChanged', 'paused', this.state.paused);
        });

        this.videoElement.addEventListener('pause', () => {
            this.state.paused = true;
            this.events.emit('propChanged', 'paused', this.state.paused);
        });

        this.videoElement.addEventListener('ended', () => {
            this.events.emit('ended');
        });

        this.videoElement.addEventListener('volumechange', () => {
            this.state.volume = this.videoElement.volume;
            this.state.muted = this.videoElement.muted;
            this.events.emit('propChanged', 'volume', this.state.volume);
            this.events.emit('propChanged', 'muted', this.state.muted);
        });

        this.videoElement.addEventListener('progress', () => {
            const buffered = [];
            for (let i = 0; i < this.videoElement.buffered.length; i++) {
                buffered.push({
                    start: this.videoElement.buffered.start(i),
                    end: this.videoElement.buffered.end(i)
                });
            }
            this.state.buffered = buffered;
            this.events.emit('propChanged', 'buffered', this.state.buffered);
        });

        this.videoElement.addEventListener('waiting', () => {
            this.state.buffering = true;
            this.events.emit('propChanged', 'buffering', this.state.buffering);
        });

        this.videoElement.addEventListener('playing', () => {
            this.state.buffering = false;
            this.events.emit('propChanged', 'buffering', this.state.buffering);
        });

        this.videoElement.addEventListener('ratechange', () => {
            this.state.playbackSpeed = this.videoElement.playbackRate;
            this.events.emit('propChanged', 'playbackSpeed', this.state.playbackSpeed);
        });
    }

    unload() {
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
            this.videoElement.removeAttribute('src');
            this.videoElement.load();
        }
        
        browserStreamingService.cleanup();
        
        this.state = {
            paused: true,
            time: 0,
            duration: 0,
            buffering: false,
            buffered: [],
            volume: 1,
            muted: false,
            playbackSpeed: 1,
            audioTracks: [],
            selectedAudioTrackId: null,
            subtitlesTracks: [],
            selectedSubtitlesTrackId: null,
            manifest: null,
            stream: null
        };
    }

    setProp(name, value) {
        if (this.videoElement) {
            switch (name) {
                case 'paused':
                    if (value) {
                        this.videoElement.pause();
                    } else {
                        this.videoElement.play().catch(console.error);
                    }
                    break;
                case 'volume':
                    this.videoElement.volume = value;
                    break;
                case 'muted':
                    this.videoElement.muted = value;
                    break;
                case 'playbackSpeed':
                    this.videoElement.playbackRate = value;
                    break;
                case 'time':
                    this.videoElement.currentTime = value;
                    break;
            }
        }
        
        this.state[name] = value;
    }

    observeProp(name) {
        // Emit current value for observed property
        if (this.state[name] !== undefined) {
            this.events.emit('propValue', name, this.state[name]);
        }
    }

    addExtraSubtitlesTracks(tracks) {
        // Store subtitles tracks
        this.state.subtitlesTracks = tracks;
        this.events.emit('propChanged', 'subtitlesTracks', this.state.subtitlesTracks);
    }

    addLocalSubtitles(filename, buffer) {
        // Handle local subtitles
        console.log('Adding local subtitles:', filename);
        // This would need additional implementation for subtitle parsing
    }

    destroy() {
        this.unload();
        if (this.videoElement && this.containerElement) {
            this.containerElement.removeChild(this.videoElement);
        }
        this.events.removeAllListeners();
    }
}

module.exports = BrowserVideo;